import React, { useRef, useState } from "react";
import { Image } from "@/components/atoms/Image";
import { Skeleton } from "@/components/atoms/Skeleton";
import { Text } from "@/components/atoms/Text";
import {
  Carousel,
  CarouselItem,
  CarouselContent,
  CarouselPrevious,
  CarouselNext,
  DotNavigation,
  type CarouselApi,
} from "@/components/molecules/Carousel";
import { useLazyLoadingCarouselImages } from "@/lib/hooks/use-lazy-loading-carousel-images";
import { cn } from "@/lib/utils";
import { PriceBox } from "./Pricebox";
import { Saved } from "./Saved";
import StarsAndGuests from "./StarsAndGuests";

interface RecommendedListingThumbnailProps {
  imageHeight?: number;
  listing: {
    _id: string;
    images: { urlSmall: string; external_id: string }[];
    price_hourly: number;
    currency: string;
    currency_symbol: string;
    is_instant_book_active: boolean;
    title: string;
    neighborhood: string;
    country: string;
    city: string;
    zip: string;
    space_review_stars?: number;
    space_review_count?: number;
    number_guests?: number;
    description: string;
    hasReviews: boolean;
    reviewValue?: number;
    reviewCount?: number;
    hasAddress: boolean;
    state: string;
    image: string;
    imageURLBase: string;
  };
  interaction?: (listingInfo: {
    listingId: string;
    listingImg: string;
    listingTitle: string;
    listingLocation: string;
    listingCapacity: number;
  }) => void;
  hideInfoBox?: boolean;
  openInSameTab?: boolean;
  onThumbnailClick?: () => void;
  isSaved?: boolean;
  pricePerHourText?: string;
  displayFromText?: string;
  displayFrom?: boolean;
}

export const RecommendedListingThumbnail: React.FC<
  RecommendedListingThumbnailProps
> = ({
  imageHeight = 225,
  listing,
  interaction = () => {},
  hideInfoBox = false,
  openInSameTab = false,
  onThumbnailClick = () => {},
  isSaved = false,
  pricePerHourText = "/hr",
  displayFromText = "from",
  displayFrom = true,
}) => {
  const imageHeightClass = imageHeight
    ? `tw-max-h-[${imageHeight}px]`
    : "tw-max-h-[225px]";
  const listingThumbnailRef = useRef<HTMLDivElement>(null);
  const [api, setApi] = useState<CarouselApi>();
  const { isVisible, loadedImages } = useLazyLoadingCarouselImages(
    listingThumbnailRef,
    listing.images?.map((image) => image.urlSmall) ?? [],
    api,
  );
  const shortenedDescription = listing.description?.slice(0, 5000);
  // build urlSmall from external_id
  function getImageUrl(imageId: string) {
    return `https://res.cloudinary.com/peerspace-inc/image/upload/q_80,c_crop,g_custom/w_420,h_260,c_fill/${imageId}`;
  }
  return (
    <div
      itemScope
      itemType="http://schema.org/LocalBusiness"
      data-testid="listing-thumbnail-component"
      ref={listingThumbnailRef}
    >
      <meta itemProp="name" content={listing.title} />
      {listing.description && shortenedDescription && (
        <meta itemProp="description" content={shortenedDescription} />
      )}
      {listing.hasReviews && (
        <div
          itemProp="aggregateRating"
          itemScope
          itemType="http://schema.org/AggregateRating"
        >
          <meta
            itemProp="ratingValue"
            content={listing.reviewValue?.toString()}
          />
          <meta
            itemProp="reviewCount"
            content={listing.reviewCount?.toString()}
          />
        </div>
      )}
      <div
        itemProp="address"
        itemScope
        itemType="http://schema.org/PostalAddress"
      >
        <meta itemProp="addressCountry" content={listing?.country} />
        <meta itemProp="addressRegion" content={listing?.state} />
        <meta itemProp="addressLocality" content={listing?.city} />
        <meta itemProp="postalCode" content={listing?.zip} />
      </div>
      {listing.image && (
        <meta
          itemProp="image"
          content={`${listing.imageURLBase}/${listing.image}`}
        />
      )}
      <div className="tw-h-full tw-aspect-[3/2]">
        <Carousel
          totalItems={listing.images?.length}
          className="tw-relative tw-group/thumbnail !tw-h-full"
          setApi={setApi}
        >
          <Saved
            isSaved={isSaved}
            interaction={() =>
              interaction({
                listingId: listing._id,
                listingImg: listing.images?.[0]?.external_id ?? "",
                listingTitle: listing.title,
                listingLocation: listing.neighborhood,
                listingCapacity: listing?.number_guests || 0,
              })
            }
          />
          <div className={cn(imageHeightClass, "tw-h-full")}>
            {/* eslint-disable-next-line react/jsx-no-target-blank -- target/rel set conditionally */}
            <a
              target={openInSameTab ? "" : "_blank"}
              rel={!openInSameTab ? "noopener" : ""}
              href={listing._id || "#"}
              onClick={onThumbnailClick}
            >
              <CarouselContent className="tw-h-full">
                {listing.images?.length > 0 ? (
                  listing.images?.map((image, index) => (
                    <CarouselItem
                      key={index}
                      index={index}
                      className={imageHeightClass}
                    >
                      {isVisible &&
                      (loadedImages.has(image.urlSmall) ||
                        loadedImages.has(image.external_id)) ? (
                        <Image
                          alt="carousel"
                          className="tw-h-full tw-w-full tw-max-w-full tw-object-cover tw-rounded-4 group-hover/thumbnail:tw-opacity-80"
                          src={
                            image.urlSmall ||
                            getImageUrl(image.external_id) ||
                            ""
                          }
                          role="img"
                          loading="lazy"
                          errorFallback={
                            <div className="tw-aspect-[3/2]">
                              <Image
                                src={getImageUrl(
                                  "v1506548002/listing-placeholder_zmaod9",
                                )}
                                className="tw-h-[100%] tw-w-full tw-max-w-full tw-object-contain tw-bg-gray-300 tw-rounded-4 group-hover/thumbnail:tw-opacity-80"
                                alt="Listing Placeholder"
                              />
                            </div>
                          }
                        />
                      ) : (
                        <Skeleton />
                      )}
                    </CarouselItem>
                  ))
                ) : (
                  <CarouselItem key={0} index={0} className={imageHeightClass}>
                    <Image
                      alt="Listing Placeholder"
                      className="tw-w-full tw-max-w-full tw-object-cover tw-rounded-4 group-hover/thumbnail:tw-opacity-80"
                      src={getImageUrl(
                        "v1506548002/listing-placeholder_zmaod9",
                      )}
                      role="img"
                      loading="lazy"
                    />
                  </CarouselItem>
                )}
              </CarouselContent>
              <div className="tw-absolute tw-bottom-10 tw-w-full tw-flex tw-justify-end tw-h-full tw-items-end tw-right-12 tw-bottom-12">
                <PriceBox
                  price={listing.price_hourly || 0}
                  currency={listing.currency || "USD"}
                  displayFrom={displayFrom}
                  displayFromText={displayFromText}
                  showInstaBook={listing.is_instant_book_active || false}
                  pricePerHourText={pricePerHourText}
                />
              </div>
            </a>
            <div className="tw-absolute tw-bottom-2 tw-w-full tw-flex tw-justify-center">
              <DotNavigation totalItems={listing.images?.length} />
            </div>
            {listing.images?.length > 1 && (
              <div className="tw-hidden tw-text-base-white group-hover/thumbnail:tw-block tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-between tw-pointer-events-none">
                <CarouselPrevious />
                <CarouselNext />
              </div>
            )}
          </div>
        </Carousel>
      </div>
      {!hideInfoBox && (
        <div className="tw-mt-16">
          <Text typeface="body-2-m" className="tw-block">
            {listing.title || ""}
          </Text>
          <Text className="tw-text-gray-600 tw-block">
            {listing.neighborhood || ""}
            {listing.neighborhood && listing.city ? ", " : ""}{" "}
            {listing.city || ""}
          </Text>
          <StarsAndGuests listing={listing} />
        </div>
      )}
    </div>
  );
};
